import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import RupiahAmountField from "../common/RupiahAmountField";
import {
  approveOrderWithOverrideApi,
  type ApproveOrderWithOverridePayload,
} from "../../api/ordersApi";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import {
  ORDER_EXPENSE_TYPE_LABEL_KEYS,
  type Order,
  type OrderExpenseLine,
  type SingletonOrderExpenseType,
} from "../../types/order";
import { getErrorMessage } from "../../utils/errorHandling";
import { formatRupiah } from "../../utils/formatRupiah";
import { toastEmitter } from "../../utils/toastEmitter";

type TranslationKeys = keyof typeof translations.en;

interface ApproveOverrideDialogProps {
  order: Order | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

const parseOverrideNumber = (value: string): number | undefined => {
  if (value.trim() === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const expenseLineLabel = (line: OrderExpenseLine, t: (key: TranslationKeys) => string): string => {
  if (line.type === "additional") {
    return line.notes || t("orders.additionalExpenses" as TranslationKeys);
  }
  return t(
    ORDER_EXPENSE_TYPE_LABEL_KEYS[line.type as SingletonOrderExpenseType] as TranslationKeys,
  );
};

const overrideFieldTestId = (line: OrderExpenseLine): string => {
  if (line.type === "additional") {
    return TestId.UJR.APPROVE_OVERRIDE_LINE(`additional-${line.id}`);
  }
  return TestId.UJR.APPROVE_OVERRIDE_LINE(line.type);
};

const ApproveOverrideDialog: React.FC<ApproveOverrideDialogProps> = ({
  order,
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const overrideableLines = useMemo(
    () =>
      (order?.expenses ?? []).filter((line): line is OrderExpenseLine & { id: string } =>
        Boolean(line.id),
      ),
    [order],
  );
  const [overrideAmounts, setOverrideAmounts] = useState<Record<string, string>>({});
  const [initialAmounts, setInitialAmounts] = useState<Record<string, string>>({});
  const [overrideNotes, setOverrideNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setOverrideAmounts({});
      setOverrideNotes("");
      setError(null);
      return;
    }

    const initial: Record<string, string> = {};
    for (const line of overrideableLines) {
      initial[line.id] =
        line.overrideAmount !== undefined && line.overrideAmount !== null
          ? String(line.overrideAmount)
          : "";
    }
    setOverrideAmounts(initial);
    setInitialAmounts(initial);
    setOverrideNotes(order?.overrideNotes ?? "");
    setError(null);
  }, [open, overrideableLines, order]);

  const payload: ApproveOrderWithOverridePayload = useMemo(() => {
    const expenseOverrides = overrideableLines
      .map((line) => {
        if (!line.id) {
          return null;
        }
        const raw = overrideAmounts[line.id] ?? "";
        const overrideAmount = parseOverrideNumber(raw);
        if (overrideAmount !== undefined) {
          return { id: line.id, overrideAmount };
        }
        if ((initialAmounts[line.id] ?? "").trim() !== "" && raw.trim() === "") {
          return { id: line.id, overrideAmount: null };
        }
        return null;
      })
      .filter((item): item is { id: string; overrideAmount: number | null } => item !== null);

    const next: ApproveOrderWithOverridePayload = {};
    if (expenseOverrides.length > 0) {
      next.expenseOverrides = expenseOverrides;
    }
    const trimmedNotes = overrideNotes.trim();
    if (trimmedNotes) {
      next.overrideNotes = trimmedNotes;
    }
    return next;
  }, [initialAmounts, overrideAmounts, overrideNotes, overrideableLines]);

  const hasPositiveOverride = (payload.expenseOverrides ?? []).some(
    (item) => (item.overrideAmount ?? 0) > 0,
  );
  const canSubmit =
    (payload.expenseOverrides?.length ?? 0) > 0 &&
    (!hasPositiveOverride || !!payload.overrideNotes);

  const handleSubmit = async () => {
    if (!order || !canSubmit) {
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await approveOrderWithOverrideApi(order._id, payload);
      await onSuccess();
      toastEmitter.emit(t("orders.approvedSuccessfully" as TranslationKeys), "success");
      onClose();
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "approveOrderWithOverride");
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-testid={TestId.UJR.APPROVE_OVERRIDE_DIALOG}
    >
      <DialogTitle>{t("orders.approveWithOverride" as TranslationKeys)}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("orders.approveWithOverrideDialogHint" as TranslationKeys)}
        </Typography>
        <Stack spacing={2}>
          {overrideableLines.map((line) => (
            <Box key={line.id}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                {expenseLineLabel(line, t)} — {t("orders.originalAmount" as TranslationKeys)}:{" "}
                {formatRupiah(line.amount)}
              </Typography>
              <RupiahAmountField
                fullWidth
                label={t("orders.overrideAmount" as TranslationKeys)}
                value={overrideAmounts[line.id] ?? ""}
                onValueChange={(value) =>
                  setOverrideAmounts((current) => ({
                    ...current,
                    [line.id]: value,
                  }))
                }
                inputProps={{ "data-testid": overrideFieldTestId(line) }}
              />
            </Box>
          ))}
          <TextField
            fullWidth
            multiline
            minRows={3}
            required={hasPositiveOverride}
            label={t("orders.overrideNotes" as TranslationKeys)}
            value={overrideNotes}
            onChange={(event) => setOverrideNotes(event.target.value)}
            helperText={
              hasPositiveOverride ? t("orders.overrideNotesRequiredHint" as TranslationKeys) : ""
            }
            inputProps={{ "data-testid": TestId.UJR.APPROVE_OVERRIDE_NOTES }}
          />
        </Stack>
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t("Cancel" as TranslationKeys)}
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={submitting || !canSubmit}
          onClick={handleSubmit}
          data-testid={TestId.UJR.APPROVE_OVERRIDE_SUBMIT}
        >
          {t("orders.approveWithOverride" as TranslationKeys)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApproveOverrideDialog;
